<template>
  <div class="content-wrapper ataviti-home">

    <!-- LOADING -->
    <div
      v-if="loading"
      class="content-body"
    >
      <div class="chantier-state">
        <b-spinner class="mb-1" />
        <p class="mt-1 mb-0">
          Chargement du chantier…
        </p>
      </div>
    </div>

    <!-- ERROR -->
    <div
      v-else-if="error"
      class="content-body"
    >
      <div class="chantier-state chantier-state--error">
        <feather-icon
          icon="AlertTriangleIcon"
          size="32"
        />
        <p class="mt-1 mb-1">
          {{ error }}
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="loadChantier"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Réessayer
        </b-button>
      </div>
    </div>

    <div
      v-else-if="chantier"
      class="content-body"
    >

      <!-- Back link -->
      <router-link
        :to="{ name: 'chantiers-list' }"
        class="chantier-back"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="14"
          class="mr-50"
        />
        Retour aux chantiers
      </router-link>

      <!-- HERO -->
      <header class="ataviti-home__banner">
        <div class="ataviti-home__banner-text">
          <p class="ataviti-home__banner-eyebrow">
            Chantier qualité
          </p>
          <div class="chantier-header__title-row">
            <h1 class="ataviti-home__title">
              {{ chantier.nom }}
            </h1>
            <span class="chantier-header__visibilite">
              <feather-icon
                :icon="chantier.visibilite === 1 ? 'GlobeIcon' : 'LockIcon'"
                size="13"
                class="mr-25"
              />
              {{ chantier.visibilite === 1 ? 'Public' : 'Privé' }}
            </span>
          </div>
          <div class="chantier-header__meta-groups">
            <!-- Classification -->
            <div class="chantier-header__group">
              <span class="chantier-header__group-label">Classification</span>
              <div class="chantier-header__badges">
                <span
                  class="badge chantier-header__cat-badge"
                  :style="categoryBadgeStyle(chantier.categorieCouleur)"
                >{{ chantier.categorieLibelle }}</span>
                <span
                  v-if="chantier.sousCategorie"
                  class="badge chantier-header__cat-badge chantier-header__origine"
                >{{ chantier.sousCategorie }}</span>
                <span class="badge chantier-header__cat-badge chantier-header__origine">
                  Origine&nbsp;: {{ chantier.origineLibelle }}
                </span>
              </div>
            </div>

            <!-- Priorité (le statut passe en grand sous le bandeau) -->
            <div
              v-if="chantier.priorite != null && prioriteLabel(chantier.priorite)"
              class="chantier-header__group"
            >
              <span class="chantier-header__group-label">Priorité</span>
              <div class="chantier-header__badges">
                <span
                  class="badge chantier-header__statut"
                  :style="prioriteBadgeStyle(chantier.priorite)"
                >{{ prioriteLabel(chantier.priorite) }}</span>
              </div>
            </div>
          </div>

          <!-- Responsable : visible par tous ceux qui ont accès au chantier (le backend le fournit). -->
          <div
            v-if="chantier.responsable"
            class="chantier-header__responsable"
          >
            <feather-icon
              icon="UserIcon"
              size="14"
              class="mr-50"
            />
            Responsable&nbsp;: {{ participantDisplayName(chantier.responsable) }}
          </div>
        </div>

        <div
          v-if="canManage || $can('CHANTIER:read')"
          class="chantier-header__actions"
        >
          <b-button
            v-if="$can('CHANTIER:read')"
            variant="primary"
            size="sm"
            :disabled="loadingRecap"
            @click="downloadRecapPdf"
          >
            <b-spinner
              v-if="loadingRecap"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="DownloadIcon"
              size="14"
              class="mr-50"
            />
            {{ loadingRecap ? 'Génération…' : 'PDF récapitulatif' }}
          </b-button>
          <b-button
            v-if="$can('CHANTIER:read')"
            variant="outline-primary"
            size="sm"
            :disabled="loadingAvancements"
            @click="openAvancementsModal"
          >
            <b-spinner
              v-if="loadingAvancements"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="TrendingUpIcon"
              size="14"
              class="mr-50"
            />
            {{ loadingAvancements ? 'Génération…' : 'PDF avancements' }}
          </b-button>
          <b-button
            v-if="canManage && $can('CHANTIER:update') && !isArchived"
            variant="outline-primary"
            size="sm"
            @click="openEditModal"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-50"
            />
            Modifier
          </b-button>
          <b-button
            v-if="canManage && $can('CHANTIER:delete')"
            variant="outline-danger"
            size="sm"
            @click="openDeleteModal"
          >
            <feather-icon
              icon="Trash2Icon"
              size="14"
              class="mr-50"
            />
            Supprimer
          </b-button>
          <!-- Archivage : réservé aux admins chantier. Bascule selon l'état archivé du chantier. -->
          <b-button
            v-if="$can('CHANTIER:admin') && !chantier.archivedAt"
            variant="outline-warning"
            size="sm"
            @click="confirmArchive"
          >
            <feather-icon
              icon="ArchiveIcon"
              size="14"
              class="mr-50"
            />
            Archiver
          </b-button>
          <b-button
            v-if="$can('CHANTIER:admin') && chantier.archivedAt"
            variant="outline-success"
            size="sm"
            @click="confirmUnarchive"
          >
            <feather-icon
              icon="RotateCcwIcon"
              size="14"
              class="mr-50"
            />
            Désarchiver
          </b-button>
        </div>
      </header>

      <!-- Bandeau « archivé » : visible uniquement par les admins (seuls à pouvoir ouvrir un chantier archivé). -->
      <b-alert
        :show="!!chantier.archivedAt"
        variant="warning"
        class="mt-1"
      >
        <div class="d-flex align-items-center">
          <feather-icon
            icon="ArchiveIcon"
            size="16"
            class="mr-50"
          />
          Ce chantier est archivé{{ chantier.archivedAt ? ` (le ${formatArchivedAt(chantier.archivedAt)})` : '' }}. Désarchivez-le pour le rendre à nouveau modifiable et visible dans la liste.
        </div>
      </b-alert>

      <!-- Statut en grand : 1er élément sous le bandeau, état compris au premier coup d'œil -->
      <chantier-statut-stepper
        :statuts="statuts"
        :current="chantier.statut"
      />

      <!-- Description -->
      <section class="chantier-section">
        <h2 class="chantier-section__title">
          <feather-icon
            icon="FileTextIcon"
            size="18"
            class="mr-50"
          />
          Description
        </h2>
        <p class="chantier-section__text">
          {{ chantier.description || 'Aucune description.' }}
        </p>
        <div
          v-if="chantier.dateDebut"
          class="chantier-section__meta"
        >
          <feather-icon
            icon="PlayIcon"
            size="14"
            class="mr-50"
          />
          Début&nbsp;: {{ formatDate(chantier.dateDebut) }}
        </div>
        <div
          v-if="chantier.dateEcheance"
          class="chantier-section__meta"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
            class="mr-50"
          />
          Échéance&nbsp;: {{ formatDate(chantier.dateEcheance) }}
        </div>
        <div
          v-if="chantier.dateCloture"
          class="chantier-section__meta"
        >
          <feather-icon
            icon="CheckCircleIcon"
            size="14"
            class="mr-50"
          />
          Date de fin réelle&nbsp;: {{ formatDate(chantier.dateCloture) }}
        </div>
        <div
          v-if="chantier.livrables"
          class="chantier-livrables"
        >
          <div class="chantier-section__meta">
            <feather-icon
              icon="PackageIcon"
              size="14"
              class="mr-50"
            />
            Livrables
          </div>
          <p class="chantier-section__text chantier-livrables__text">
            {{ chantier.livrables }}
          </p>
        </div>
      </section>

      <!-- Notes (édition réservée aux gestionnaires ; masquée si vide pour les lecteurs) -->
      <section
        v-if="canEdit || chantier.notes"
        class="chantier-section"
      >
        <div class="chantier-section__head">
          <h2 class="chantier-section__title">
            <feather-icon
              icon="ClipboardIcon"
              size="18"
              class="mr-50"
            />
            Notes
          </h2>
          <b-button
            v-if="!editingNotes && canManage && $can('CHANTIER:update') && !isArchived"
            variant="outline-primary"
            size="sm"
            @click="startEditNotes"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-50"
            />
            {{ chantier.notes ? 'Modifier' : 'Ajouter des notes' }}
          </b-button>
        </div>

        <template v-if="!editingNotes">
          <p
            v-if="!chantier.notes"
            class="chantier-section__text chantier-empty-inline"
          >
            Aucune note.
          </p>
          <p
            v-else
            class="chantier-section__text chantier-livrables__text"
          >
            {{ chantier.notes }}
          </p>
        </template>

        <template v-else>
          <b-form-textarea
            id="notes-edit"
            v-model="notesDraft"
            rows="6"
            placeholder="Visibles par toute personne ayant accès au chantier."
          />
          <p
            v-if="notesError"
            class="text-danger mt-50 mb-0"
          >
            {{ notesError }}
          </p>
          <div
            class="d-flex justify-content-end mt-1"
            style="gap: 8px;"
          >
            <b-button
              variant="outline-secondary"
              size="sm"
              :disabled="savingNotes"
              @click="cancelEditNotes"
            >
              Annuler
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              :disabled="savingNotes"
              @click="submitNotes"
            >
              Enregistrer
            </b-button>
          </div>
        </template>
      </section>

      <!-- Participants -->
      <section
        v-if="canSeeInternal"
        class="chantier-section"
      >
        <div class="chantier-section__head">
          <h2 class="chantier-section__title">
            <feather-icon
              icon="UsersIcon"
              size="18"
              class="mr-50"
            />
            Participants
            <span class="chantier-section__count">{{ participants.length }}</span>
          </h2>
          <b-button
            v-if="canManageParticipants"
            variant="outline-primary"
            size="sm"
            @click="openParticipantsModal"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-50"
            />
            Gérer
          </b-button>
        </div>

        <p
          v-if="!participants.length"
          class="chantier-section__text chantier-empty-inline"
        >
          Aucun participant.
        </p>
        <div
          v-else
          class="chantier-participants"
        >
          <div
            v-for="p in participants"
            :key="p.id"
            class="chantier-participant"
          >
            <div class="chantier-participant__meta">
              <div class="chantier-participant__name">
                {{ participantDisplayName(p) }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Plans d'action -->
      <section class="chantier-section">
        <h2 class="chantier-section__title">
          <feather-icon
            icon="TargetIcon"
            size="18"
            class="mr-50"
          />
          Plans d'action
          <span class="chantier-section__count">{{ plansAction.length }}</span>
        </h2>

        <div
          v-if="canManage && $can('CHANTIER:update') && !isArchived"
          class="mb-1"
        >
          <b-button
            variant="primary"
            size="sm"
            @click="openPlanCreateModal"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Ajouter un plan d'action
          </b-button>
        </div>

        <p
          v-if="!plansAction.length"
          class="chantier-section__text chantier-empty-inline"
        >
          Aucun plan d'action.
        </p>
        <div
          v-else
          class="chantier-plans"
        >
          <article
            v-for="plan in plansAction"
            :key="plan.id"
            class="chantier-plan-card"
            @click="goToPlan(plan)"
          >
            <div class="chantier-plan-card__head">
              <h3 class="chantier-plan-card__title">
                {{ plan.libelle }}
              </h3>
              <span
                v-if="plan.processusNom"
                class="badge chantier-plan-card__processus"
              >
                <feather-icon
                  icon="GitBranchIcon"
                  size="12"
                  class="mr-25"
                />
                {{ plan.processusNom }}
              </span>
              <span
                class="badge chantier-plan-card__statut"
                :style="statutBadgeStyle(plan.statut)"
              >{{ statutLabel(plan.statut) }}</span>
            </div>

            <div class="chantier-plan-card__body">
              <div
                v-if="canSeeInternal"
                class="chantier-plan-card__responsable"
              >
                <div>
                  <div class="chantier-plan-card__responsable-label">
                    Responsable
                  </div>
                  <div class="chantier-plan-card__responsable-name">
                    {{ responsableName(plan) }}
                  </div>
                </div>
              </div>

              <div class="chantier-plan-card__metrics">
                <span class="chantier-plan-card__metric">
                  <feather-icon
                    icon="LinkIcon"
                    size="13"
                    class="mr-50"
                  />
                  {{ (plan.liens || []).length }} lien(s)
                </span>
                <span
                  v-if="canSeeInternal"
                  class="chantier-plan-card__metric"
                >
                  <feather-icon
                    icon="CalendarIcon"
                    size="13"
                    class="mr-50"
                  />
                  {{ (plan.reunionsPlanifiees || []).length }} réunion(s)
                </span>
              </div>

              <span class="chantier-plan-card__cta">
                Ouvrir
                <feather-icon
                  icon="ArrowRightIcon"
                  size="14"
                  class="ml-25"
                />
              </span>
            </div>
          </article>
        </div>
      </section>

      <!-- Mesure d'efficacité (masquée si vide pour les lecteurs) -->
      <section
        v-if="canEdit || mesureEfficacite"
        class="chantier-section"
      >
        <div class="chantier-section__head">
          <h2 class="chantier-section__title">
            <feather-icon
              icon="BarChart2Icon"
              size="18"
              class="mr-50"
            />
            Mesure d'efficacité
          </h2>
          <div
            v-if="canManage && $can('CHANTIER:update') && !isArchived"
            class="d-flex"
            style="gap: 8px;"
          >
            <b-button
              variant="outline-primary"
              size="sm"
              @click="openMesureModal"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-50"
              />
              {{ mesureEfficacite ? 'Modifier' : 'Renseigner la mesure' }}
            </b-button>
            <b-button
              v-if="mesureEfficacite"
              variant="flat-danger"
              size="sm"
              aria-label="Supprimer la mesure d'efficacité"
              @click="confirmDeleteMesure"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
                aria-hidden="true"
              />
            </b-button>
          </div>
        </div>

        <p
          v-if="!mesureEfficacite"
          class="chantier-section__text chantier-empty-inline"
        >
          Aucune mesure d'efficacité renseignée.
        </p>
        <div v-else>
          <div class="chantier-section__meta">
            <feather-icon
              icon="CalendarIcon"
              size="14"
              class="mr-50"
            />
            Mesurée le&nbsp;: {{ formatDate(mesureEfficacite.dateMesure) }}
          </div>
          <div class="chantier-section__meta">
            <span
              class="badge chantier-header__statut"
              :style="efficaciteBadge.style"
            >{{ efficaciteBadge.label }}</span>
          </div>
          <div class="chantier-livrables">
            <div class="chantier-section__meta">
              <feather-icon
                icon="ActivityIcon"
                size="14"
                class="mr-50"
              />
              Indicateurs d'efficacité
            </div>
            <p class="chantier-section__text chantier-livrables__text">
              {{ mesureEfficacite.indicateurs }}
            </p>
          </div>
          <div
            v-if="mesureEfficacite.estEfficace === Efficacite.Non && mesureEfficacite.actionsAmelioration"
            class="chantier-livrables"
          >
            <div class="chantier-section__meta">
              <feather-icon
                icon="ToolIcon"
                size="14"
                class="mr-50"
              />
              Actions d'amélioration
            </div>
            <p class="chantier-section__text chantier-livrables__text">
              {{ mesureEfficacite.actionsAmelioration }}
            </p>
          </div>
          <div
            v-if="mesureEfficacite.estEfficace === Efficacite.Non && enfants.length"
            class="chantier-section__meta"
          >
            <feather-icon
              icon="GitBranchIcon"
              size="14"
              class="mr-50"
            />
            Chantier(s) de suivi&nbsp;: {{ enfants.map(e => e.nom).join(', ') }}
          </div>
        </div>
      </section>

      <!-- Appartenance : chantier parent + chantiers de suivi (lecture seule ; le parent se définit à la création / édition ; masquée si vide pour les lecteurs) -->
      <section
        v-if="canEdit || parentChantier || enfants.length"
        class="chantier-section"
      >
        <h2 class="chantier-section__title">
          <feather-icon
            icon="GitBranchIcon"
            size="18"
            class="mr-50"
          />
          Appartenance
        </h2>

        <div class="chantier-liens-group">
          <div class="chantier-liens-group__label">
            Chantier parent
          </div>
          <p
            v-if="!parentChantier"
            class="chantier-section__text chantier-empty-inline"
          >
            Aucun chantier parent.
          </p>
          <ul
            v-else
            class="chantier-liens"
          >
            <li class="chantier-lien">
              <router-link
                :to="{ name: 'chantier-detail', params: { id: parentChantier.id } }"
                class="chantier-lien__link"
              >{{ parentChantier.nom }}</router-link>
              <span
                class="badge chantier-header__statut"
                :style="statutBadgeStyle(parentChantier.statut)"
              >{{ statutLabel(parentChantier.statut) }}</span>
            </li>
          </ul>
        </div>

        <div class="chantier-liens-group">
          <div class="chantier-liens-group__label">
            Chantiers de suivi ({{ enfants.length }})
          </div>
          <p
            v-if="!enfants.length"
            class="chantier-section__text chantier-empty-inline"
          >
            Aucun chantier de suivi.
          </p>
          <ul
            v-else
            class="chantier-liens"
          >
            <li
              v-for="enfant in enfants"
              :key="enfant.id"
              class="chantier-lien"
            >
              <router-link
                :to="{ name: 'chantier-detail', params: { id: enfant.id } }"
                class="chantier-lien__link"
              >{{ enfant.nom }}</router-link>
              <span
                class="badge chantier-header__statut"
                :style="statutBadgeStyle(enfant.statut)"
              >{{ statutLabel(enfant.statut) }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- EDIT MODAL -->
      <b-modal
        id="chantier-edit-modal"
        title="Modifier le chantier"
        ok-title="Enregistrer"
        cancel-title="Annuler"
        :ok-disabled="saving"
        centered
        size="lg"
        @ok.prevent="submitEdit"
        :no-close-on-backdrop="true"
      >
        <b-form-group
          label="Nom"
          label-for="edit-nom"
        >
          <b-form-input
            id="edit-nom"
            v-model="editModel.nom"
          />
        </b-form-group>
        <b-form-group
          label="Description"
          label-for="edit-desc"
        >
          <b-form-textarea
            id="edit-desc"
            v-model="editModel.description"
            rows="3"
          />
        </b-form-group>
        <b-form-group label="Responsable">
          <searchable-vue-select
            v-model="editResponsable"
            :options="listUtilisateurs"
            label="nomComplet"
            :has-next-page="listUtilisateurs.length < utilisateursTotalCount"
            placeholder="Choisir un responsable"
            @option:clear="searchUtilisateurs()"
            @load-next-page="(params) => searchUtilisateurs(params)"
          />
        </b-form-group>
        <b-form-group label="Chantier parent (optionnel)">
          <searchable-vue-select
            v-model="editParent"
            :options="parentPickerOptions"
            label="nom"
            :has-next-page="listChantiers.length < chantiersTotalCount"
            placeholder="Rattacher à un chantier parent"
            @option:clear="searchChantiers()"
            @load-next-page="(params) => searchChantiers(params)"
          />
        </b-form-group>
        <b-form-group
          label="Projet d'amélioration continue (optionnel)"
          label-for="edit-projet"
        >
          <b-form-select
            id="edit-projet"
            v-model="editModel.projetId"
            :options="projetOptions"
          />
        </b-form-group>
        <div class="row">
          <div class="col-12 col-md-6">
            <b-form-group
              label="Catégorie"
              label-for="edit-cat"
            >
              <b-form-select
                id="edit-cat"
                v-model="editModel.categorieId"
                :options="categorieOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Origine"
              label-for="edit-origine"
            >
              <b-form-select
                id="edit-origine"
                v-model="editModel.origineId"
                :options="origineOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Sous-catégorie"
              label-for="edit-subcat"
            >
              <b-form-input
                id="edit-subcat"
                v-model="editModel.sousCategorie"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Statut"
              label-for="edit-statut"
            >
              <b-form-select
                id="edit-statut"
                v-model="editModel.statut"
                :options="statutOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Visibilité"
              label-for="edit-vis"
            >
              <b-form-select
                id="edit-vis"
                v-model="editModel.visibilite"
                :options="visibiliteOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Priorité"
              label-for="edit-priorite"
            >
              <b-form-select
                id="edit-priorite"
                v-model="editModel.priorite"
                :options="prioriteOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Date de début"
              label-for="edit-datedebut"
            >
              <b-form-input
                id="edit-datedebut"
                v-model="editModel.dateDebut"
                type="date"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Échéance"
              label-for="edit-echeance"
            >
              <b-form-input
                id="edit-echeance"
                v-model="editModel.dateEcheance"
                type="date"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-6">
            <b-form-group
              label="Date de fin réelle"
              label-for="edit-datefin"
            >
              <b-form-input
                id="edit-datefin"
                v-model="editModel.dateCloture"
                type="date"
              />
            </b-form-group>
          </div>
        </div>
        <b-form-group
          label="Livrables"
          label-for="edit-livrables"
        >
          <b-form-textarea
            id="edit-livrables"
            v-model="editModel.livrables"
            rows="3"
          />
        </b-form-group>
        <p
          v-if="modalError"
          class="text-danger mb-0"
        >
          {{ modalError }}
        </p>
      </b-modal>

      <!-- DELETE MODAL -->
      <b-modal
        id="chantier-delete-modal"
        title="Supprimer le chantier"
        ok-title="Supprimer"
        ok-variant="danger"
        cancel-title="Annuler"
        :ok-disabled="saving"
        centered
        @ok.prevent="submitDelete"
      >
        <p>Voulez-vous vraiment supprimer le chantier « {{ chantier.nom }} » ? Cette action est irréversible.</p>
        <p
          v-if="modalError"
          class="text-danger mb-0"
        >
          {{ modalError }}
        </p>
      </b-modal>

      <!-- PDF AVANCEMENTS (récap entre 2 dates) MODAL -->
      <b-modal
        id="modal-avancements-pdf"
        title="Récap d'avancements entre 2 dates"
        ok-title="Générer le PDF"
        cancel-title="Annuler"
        :ok-disabled="!avancementsDateDebut || !avancementsDateFin || loadingAvancements"
        centered
        @ok.prevent="downloadAvancementsPdf"
      >
        <p class="text-muted mb-2">
          Consolidez les avancements (notes filtrées par date) sur une période précise.
        </p>
        <b-form-group
          label="Date de début"
          label-for="avancements-date-debut"
        >
          <b-form-datepicker
            id="avancements-date-debut"
            v-model="avancementsDateDebut"
            :max="avancementsDateFin || undefined"
            placeholder="Date de début…"
            locale="fr"
            start-weekday="1"
          />
        </b-form-group>
        <b-form-group
          label="Date de fin"
          label-for="avancements-date-fin"
        >
          <b-form-datepicker
            id="avancements-date-fin"
            v-model="avancementsDateFin"
            :min="avancementsDateDebut || undefined"
            placeholder="Date de fin…"
            locale="fr"
            start-weekday="1"
          />
        </b-form-group>
      </b-modal>

      <!-- PARTICIPANTS MODAL -->
      <b-modal
        id="chantier-participants-modal"
        title="Gérer les participants"
        ok-title="Enregistrer"
        cancel-title="Annuler"
        :ok-disabled="saving"
        centered
        @ok.prevent="submitParticipants"
        :no-close-on-backdrop="true"
      >
        <b-form-group label="Participants">
          <searchable-vue-select
            v-model="selectedParticipants"
            placeholder="Rechercher des utilisateurs"
            :options="listUtilisateurs"
            label="nomComplet"
            multiple
            :has-next-page="listUtilisateurs.length < utilisateursTotalCount"
            @option:clear="searchUtilisateurs()"
            @load-next-page="(params) => searchUtilisateurs(params)"
          />
        </b-form-group>
        <p
          v-if="modalError"
          class="text-danger mb-0"
        >
          {{ modalError }}
        </p>
      </b-modal>

      <!-- CREATE PLAN MODAL -->
      <b-modal
        id="plan-create-modal"
        title="Nouveau plan d'action"
        ok-title="Créer"
        cancel-title="Annuler"
        :ok-disabled="creatingPlan"
        centered
        size="lg"
        @ok.prevent="submitPlanCreate"
        :no-close-on-backdrop="true"
      >
        <b-form-group
          label="Libellé"
          label-for="plan-create-libelle"
        >
          <b-form-input
            id="plan-create-libelle"
            v-model="planCreateModel.libelle"
          />
        </b-form-group>

        <b-form-group
          label="Description"
          label-for="plan-create-desc"
        >
          <b-form-textarea
            id="plan-create-desc"
            v-model="planCreateModel.description"
            rows="3"
          />
        </b-form-group>

        <b-form-group label="Responsable">
          <searchable-vue-select
            v-model="selectedResponsable"
            :options="listUtilisateurs"
            label="nomComplet"
            :has-next-page="listUtilisateurs.length < utilisateursTotalCount"
            placeholder="Choisir un responsable"
            @option:clear="searchUtilisateurs()"
            @load-next-page="(params) => searchUtilisateurs(params)"
          />
        </b-form-group>

        <b-form-group label="Processus">
          <searchable-vue-select
            v-model="selectedProcessus"
            :options="processusOptions"
            label="libelle"
            :has-next-page="false"
            :load="false"
            placeholder="Processus (optionnel)"
          />
        </b-form-group>

        <b-form-group
          label="Échéance"
          label-for="plan-create-echeance"
        >
          <b-form-input
            id="plan-create-echeance"
            v-model="planCreateModel.dateEcheance"
            type="date"
          />
        </b-form-group>

        <p
          v-if="planCreateError"
          class="text-danger mb-0"
        >
          {{ planCreateError }}
        </p>
      </b-modal>

      <!-- MESURE D'EFFICACITÉ MODAL -->
      <b-modal
        id="mesure-efficacite-modal"
        :title="mesureEfficacite ? 'Modifier la mesure d\'efficacité' : 'Mesure d\'efficacité'"
        ok-title="Enregistrer"
        cancel-title="Annuler"
        :ok-disabled="savingMesure"
        centered
        @ok.prevent="submitMesure"
      >
        <b-form-group
          label="Date de mesure"
          label-for="mesure-date"
        >
          <b-form-input
            id="mesure-date"
            v-model="mesureModel.dateMesure"
            type="date"
          />
        </b-form-group>
        <b-form-group
          label="Indicateurs d'efficacité"
          label-for="mesure-indicateurs"
        >
          <b-form-textarea
            id="mesure-indicateurs"
            v-model="mesureModel.indicateurs"
            rows="3"
          />
        </b-form-group>
        <b-form-group label="Le chantier a-t-il été efficace ?">
          <b-form-radio-group
            v-model="mesureModel.estEfficace"
            :options="efficaciteOptions"
          />
        </b-form-group>
        <template v-if="mesureModel.estEfficace === Efficacite.Non">
          <b-form-group
            label="Actions d'amélioration"
            label-for="mesure-actions"
          >
            <b-form-textarea
              id="mesure-actions"
              v-model="mesureModel.actionsAmelioration"
              rows="3"
            />
          </b-form-group>
          <b-button
            variant="outline-primary"
            size="sm"
            class="mb-1"
            @click="saveMesureThenCreateChild"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Enregistrer et créer un chantier de suivi
          </b-button>
        </template>
        <p
          v-if="mesureError"
          class="text-danger mb-0"
        >
          {{ mesureError }}
        </p>
      </b-modal>

      <!-- CRÉATION DE CHANTIER (composant partagé) : utilisé pour « Créer un chantier de suivi » -->
      <chantier-create-modal
        ref="createModal"
        @created="onFollowUpCreated"
      />

    </div>

    <div
      v-else
      class="content-body"
    >
      <div class="chantier-warning">
        <feather-icon
          icon="AlertTriangleIcon"
          size="16"
          class="mr-50"
        />
        Chantier introuvable.
      </div>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BModal,
  BSpinner,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormRadioGroup,
  BFormDatepicker,
} from 'bootstrap-vue'
import SearchableVueSelect from '@/components/selects/SearchableVueSelect.vue'
import ChantierCreateModal from '@/components/chantiers/ChantierCreateModal.vue'
import ChantierStatutStepper from '@/components/chantiers/ChantierStatutStepper.vue'
import { Efficacite } from '@/api/models/chantiers/chantier'
import { toDateInput, todayDateInput, daysAgoDateInput } from './date-input'

export default {
  name: 'ChantierDetailView',
  components: {
    BAlert,
    BButton,
    BModal,
    BSpinner,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormRadioGroup,
    BFormDatepicker,
    SearchableVueSelect,
    ChantierCreateModal,
    ChantierStatutStepper,
  },
  data() {
    return {
      Efficacite,
      efficaciteOptions: [
        { text: 'Oui', value: Efficacite.Oui },
        { text: 'Non', value: Efficacite.Non },
        { text: 'En cours', value: Efficacite.EnCours },
      ],
      chantier: null,
      statuts: [],
      priorites: [],
      categories: [],
      origines: [],
      projets: [],
      loading: true,
      error: null,
      saving: false,
      modalError: null,
      editModel: this.emptyEditModel(),
      editResponsable: null,
      listUtilisateurs: [],
      utilisateursTotalCount: 0,
      selectedParticipants: [],
      // --- Création d'un plan d'action ---
      planCreateModel: { libelle: '', description: '', dateEcheance: '' },
      selectedResponsable: null,
      selectedProcessus: null,
      creatingPlan: false,
      planCreateError: null,
      processusOptions: [],
      // Mesure d'efficacité
      mesureModel: {
        dateMesure: '', indicateurs: '', estEfficace: Efficacite.Oui, actionsAmelioration: '',
      },
      savingMesure: false,
      mesureError: null,
      // Notes libres (carte éditée en ligne)
      editingNotes: false,
      notesDraft: '',
      savingNotes: false,
      notesError: null,
      // Filiation (picker chantier parent + liste enfants)
      listChantiers: [],
      chantiersTotalCount: 0,
      editParent: null,
      loadingRecap: false,
      // PDF avancements (récap entre 2 dates)
      loadingAvancements: false,
      avancementsDateDebut: null,
      avancementsDateFin: null,
    }
  },
  computed: {
    efficaciteBadge() {
      const e = this.mesureEfficacite && this.mesureEfficacite.estEfficace
      if (e === Efficacite.Oui) return { label: 'Efficace : Oui', style: { backgroundColor: '#1c4d4d', color: '#fff' } }
      if (e === Efficacite.EnCours) return { label: 'Efficace : En cours', style: { backgroundColor: '#fcf5a6', color: '#0F1F1F' } }
      return { label: 'Efficace : Non', style: { backgroundColor: '#ff6131', color: '#fff' } }
    },
    canSeeInternal() {
      return !!(this.chantier && this.chantier.userCanSeeInternal)
    },
    isArchived() {
      // Un chantier archivé est en lecture seule : on masque toutes les actions d'édition (désarchiver pour modifier).
      return !!(this.chantier && this.chantier.archivedAt)
    },
    canManage() {
      // Gestion = droit Update ET visibilité interne (un non-interne est en lecture seule).
      return !!(this.chantier && this.chantier.userCanManage && this.chantier.userCanSeeInternal)
    },
    canManageParticipants() {
      // Gestion des participants réservée au responsable / admin (flag calculé côté serveur) ; jamais sur un chantier archivé.
      return !!(this.chantier && this.chantier.userCanManageParticipants) && !this.isArchived
    },
    canEdit() {
      // Peut éditer une carte du chantier = même condition que les boutons d'édition (bloqué si archivé).
      return this.canManage && this.$can('CHANTIER:update') && !this.isArchived
    },
    participantIds() {
      return (this.chantier && this.chantier.participantIds) || []
    },
    participants() {
      // Participants enrichis (id + nom/prénom) renvoyés par le serveur.
      return (this.chantier && this.chantier.participants) || []
    },
    plansAction() {
      return (this.chantier && this.chantier.plansAction) || []
    },
    categorieOptions() {
      return this.categories.map(c => ({ value: c.id, text: c.libelle }))
    },
    origineOptions() {
      return this.origines.map(o => ({ value: o.id, text: o.libelle }))
    },
    statutOptions() {
      return this.statuts.map(s => ({ value: s.id, text: s.valeur }))
    },
    prioriteOptions() {
      return this.priorites.map(p => ({ value: p.id, text: p.valeur }))
    },
    projetOptions() {
      return [
        { value: null, text: 'Aucun projet' },
        ...this.projets.map(p => ({ value: p.id, text: p.nom })),
      ]
    },
    visibiliteOptions() {
      return [
        { value: 0, text: 'Privé' },
        { value: 1, text: 'Public' },
      ]
    },
    mesureEfficacite() {
      return (this.chantier && this.chantier.mesureEfficacite) || null
    },
    parentChantier() {
      return (this.chantier && this.chantier.parent) || null
    },
    enfants() {
      return (this.chantier && this.chantier.enfants) || []
    },
    parentPickerOptions() {
      // Un chantier ne peut pas être son propre parent.
      return this.listChantiers.filter(c => !this.chantier || c.id !== this.chantier.id)
    },
  },
  watch: {
    // Vue réutilise l'instance du composant lors d'une navigation chantier → chantier (même route,
    // ex. via la carte « Appartenance ») et ne rejoue donc pas created(). On recharge dès que l'id de
    // l'URL change, sinon on continue d'afficher les données du chantier précédent.
    '$route.params.id': function reloadOnChantierIdChange(newId, oldId) {
      if (newId && newId !== oldId) this.loadChantier()
    },
  },
  created() {
    this.loadChantier()
  },
  methods: {
    emptyEditModel() {
      return {
        nom: '',
        description: '',
        categorieId: null,
        origineId: null,
        sousCategorie: '',
        statut: null,
        priorite: null,
        visibilite: 0,
        dateEcheance: '',
        dateDebut: '',
        dateCloture: '',
        livrables: '',
        participantIds: [],
        projetId: null,
      }
    },
    async loadChantier() {
      this.loading = true
      this.error = null
      const { id } = this.$route.params
      try {
        const [chantier, statuts, priorites] = await Promise.all([
          this.$http.myressif.chantiers.getById(id),
          this.$http.myressif.chantiers.getStatuts(),
          this.$http.myressif.chantiers.getPriorites(),
        ])
        this.chantier = chantier
        this.statuts = statuts || []
        this.priorites = priorites || []
      } catch (e) {
        this.chantier = null
        this.error = 'Une erreur est survenue lors du chargement du chantier.'
      } finally {
        this.loading = false
      }
    },
    participantDisplayName(p) {
      if (!p) return ''
      return p.nomComplet || `${p.prenom || ''} ${p.nom || ''}`.trim() || p.id
    },
    responsableName(plan) {
      const r = plan.responsable
      if (!r) return '—'
      return r.nomComplet || `${r.prenom || ''} ${r.nom || ''}`.trim() || '—'
    },
    async loadReferentiels() {
      if (this.categories.length && this.origines.length) return
      try {
        const [categories, origines, projets] = await Promise.all([
          this.$http.myressif.chantiers.listCategories(),
          this.$http.myressif.chantiers.listOrigines(),
          this.$http.myressif.chantierProjets.listRef(),
        ])
        this.categories = (categories && categories.items) || []
        this.origines = (origines && origines.items) || []
        this.projets = projets || []
      } catch (e) {
        this.categories = []
        this.origines = []
        this.projets = []
      }
    },
    async openEditModal() {
      this.modalError = null
      await this.loadReferentiels()
      await this.searchUtilisateurs()
      this.editModel = {
        nom: this.chantier.nom || '',
        description: this.chantier.description || '',
        categorieId: this.chantier.categorieId || null,
        origineId: this.chantier.origineId || null,
        sousCategorie: this.chantier.sousCategorie || '',
        statut: this.chantier.statut,
        priorite: this.chantier.priorite != null ? this.chantier.priorite : null,
        visibilite: this.chantier.visibilite,
        dateEcheance: this.formatDateInput(this.chantier.dateEcheance),
        dateDebut: this.formatDateInput(this.chantier.dateDebut),
        dateCloture: this.formatDateInput(this.chantier.dateCloture),
        livrables: this.chantier.livrables || '',
        participantIds: [...this.participantIds],
        projetId: this.chantier.projetId || null,
      }
      this.editResponsable = this.chantier.responsable
        ? { id: this.chantier.responsable.id, nomComplet: this.participantDisplayName(this.chantier.responsable) }
        : null
      this.editParent = this.chantier.parent
        ? { id: this.chantier.parent.id, nom: this.chantier.parent.nom }
        : null
      await this.searchChantiers()
      this.$bvModal.show('chantier-edit-modal')
    },
    async submitEdit() {
      if (!this.editResponsable) {
        this.modalError = 'Le responsable est obligatoire.'
        return
      }
      this.saving = true
      this.modalError = null
      try {
        const model = {
          nom: this.editModel.nom,
          description: this.editModel.description || undefined,
          categorieId: this.editModel.categorieId,
          origineId: this.editModel.origineId,
          sousCategorie: this.editModel.sousCategorie || undefined,
          statut: this.editModel.statut,
          priorite: this.editModel.priorite != null ? this.editModel.priorite : undefined,
          visibilite: this.editModel.visibilite,
          dateEcheance: this.editModel.dateEcheance || undefined,
          responsableId: this.editResponsable.id,
          dateDebut: this.editModel.dateDebut || undefined,
          dateCloture: this.editModel.dateCloture || undefined,
          livrables: this.editModel.livrables || undefined,
          chantierParentId: this.editParent ? this.editParent.id : undefined,
          projetId: this.editModel.projetId || undefined,
          participantIds: this.editModel.participantIds,
        }
        await this.$http.myressif.chantiers.update(this.chantier.id, model)
        this.$bvModal.hide('chantier-edit-modal')
        await this.loadChantier()
        // Prompt non-bloquant : si le chantier passe à un statut « résolu » (Clôturé) sans mesure, proposer de la renseigner.
        const statutChoisi = this.statuts.find(s => s.id === this.editModel.statut)
        if (statutChoisi && statutChoisi.resolu === true && !this.mesureEfficacite) {
          this.$bvModal
            .msgBoxConfirm("Chantier clôturé. Renseigner la mesure d'efficacité maintenant ?", {
              title: "Mesure d'efficacité",
              okTitle: 'Oui',
              cancelTitle: 'Plus tard',
              cancelVariant: 'outline-secondary',
              centered: true,
            })
            .then(value => { if (value === true) this.openMesureModal() })
        }
      } catch (e) {
        this.modalError = 'La mise à jour a échoué.'
      } finally {
        this.saving = false
      }
    },
    openDeleteModal() {
      this.modalError = null
      this.$bvModal.show('chantier-delete-modal')
    },
    async submitDelete() {
      this.saving = true
      this.modalError = null
      try {
        await this.$http.myressif.chantiers.delete(this.chantier.id)
        this.$bvModal.hide('chantier-delete-modal')
        this.$router.push({ name: 'chantiers-list' })
      } catch (e) {
        this.modalError = 'La suppression a échoué.'
      } finally {
        this.saving = false
      }
    },
    // ----- Archivage (réservé aux admins chantier) -----
    formatArchivedAt(value) {
      if (!value) return ''
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR')
    },
    confirmArchive() {
      this.$bvModal
        .msgBoxConfirm(`Archiver le chantier « ${this.chantier.nom} » ? Il sera retiré de la liste, du kanban, du bulletin et des PDF, et ne restera consultable que par les admins via la page des chantiers archivés.`, {
          title: 'Confirmation d\'archivage',
          okVariant: 'warning',
          okTitle: 'Archiver',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => { if (value === true) this.doArchive() })
    },
    async doArchive() {
      try {
        await this.$http.myressif.chantiers.archive(this.chantier.id)
        await this.loadChantier()
      } catch (e) {
        this.$bvModal.msgBoxOk('L\'archivage du chantier a échoué.', { title: 'Oops', centered: true })
      }
    },
    confirmUnarchive() {
      this.$bvModal
        .msgBoxConfirm(`Désarchiver le chantier « ${this.chantier.nom} » ? Il réapparaîtra dans la liste active.`, {
          title: 'Confirmation de désarchivage',
          okVariant: 'primary',
          okTitle: 'Désarchiver',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => { if (value === true) this.doUnarchive() })
    },
    async doUnarchive() {
      try {
        await this.$http.myressif.chantiers.unarchive(this.chantier.id)
        await this.loadChantier()
      } catch (e) {
        this.$bvModal.msgBoxOk('Le désarchivage du chantier a échoué.', { title: 'Oops', centered: true })
      }
    },
    async openParticipantsModal() {
      this.modalError = null
      await this.searchUtilisateurs()
      this.selectedParticipants = this.participants.map(p => ({
        id: p.id,
        nomComplet: this.participantDisplayName(p),
      }))
      this.$bvModal.show('chantier-participants-modal')
    },
    async submitParticipants() {
      this.saving = true
      this.modalError = null
      try {
        const ids = (this.selectedParticipants || []).map(u => u.id)
        await this.$http.myressif.chantiers.setParticipants(this.chantier.id, ids)
        this.$bvModal.hide('chantier-participants-modal')
        await this.loadChantier()
      } catch (e) {
        this.modalError = 'La mise à jour des participants a échoué.'
      } finally {
        this.saving = false
      }
    },
    async searchUtilisateurs(params) {
      // Liste paginée des utilisateurs : reset (ouverture/recherche/clear) → page 1 ;
      // scroll (reset=false, pageNumber incrémenté) → on concatène les pages suivantes.
      const search = (params && params.search) || ''
      const reset = !params || params.reset
      const pageNumber = reset ? 1 : ((params && params.pageNumber) || 1)
      try {
        const response = await this.$http.myressif.utilisateurs
          .paginatedList(pageNumber, 10, search, false, 'nom', false)
        const items = (response && response.items) || []
        this.listUtilisateurs = reset
          ? items
          : this.listUtilisateurs.concat(items.filter(ri => !this.listUtilisateurs.some(e => e.id === ri.id)))
        this.utilisateursTotalCount = (response && response.totalCount) || this.listUtilisateurs.length
      } catch (e) {
        if (reset) { this.listUtilisateurs = []; this.utilisateursTotalCount = 0 }
      }
      return this.listUtilisateurs
    },
    goToPlan(plan) {
      this.$router.push({
        name: 'chantier-plan-detail',
        params: { chantierId: this.chantier.id, planId: plan.id },
      })
    },
    statutLabel(statutId) {
      const s = this.statuts.find(st => st.id === statutId)
      return s ? s.valeur : 'Inconnu'
    },
    statutBadgeStyle(statutId) {
      const s = this.statuts.find(st => st.id === statutId)
      const couleur = s ? s.couleur : '#5A7373'
      return { backgroundColor: couleur, color: '#fff' }
    },
    categoryBadgeStyle(couleur) {
      const c = couleur || '#5A7373'
      return { backgroundColor: c, color: '#fff' }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('fr-FR')
    },
    formatDateInput(dateStr) {
      // Date calendaire telle que sérialisée par le backend, sans conversion de fuseau (helper partagé
      // avec PlanActionDetailView) -> pas de décalage ±1 jour (toISOString convertissait en UTC).
      return toDateInput(dateStr)
    },
    async openPlanCreateModal() {
      this.planCreateModel = { libelle: '', description: '', dateEcheance: '' }
      this.selectedResponsable = null
      this.selectedProcessus = null
      this.planCreateError = null
      await this.searchUtilisateurs()
      await this.searchProcessus()
      this.$bvModal.show('plan-create-modal')
    },
    prioriteLabel(prioriteId) {
      const p = this.priorites.find(pr => pr.id === prioriteId)
      return p ? p.valeur : ''
    },
    prioriteBadgeStyle(prioriteId) {
      const p = this.priorites.find(pr => pr.id === prioriteId)
      const couleur = p ? p.couleur : '#5A7373'
      return { backgroundColor: couleur, color: '#fff' }
    },
    async searchProcessus() {
      try {
        const response = await this.$http.myressif.chantiers.listProcessus()
        this.processusOptions = (response && response.items) || []
      } catch (e) {
        this.processusOptions = []
      }
    },
    async submitPlanCreate() {
      if (!this.planCreateModel.libelle.trim() || !this.selectedResponsable) {
        this.planCreateError = 'Le libellé et le responsable sont obligatoires.'
        return
      }
      this.creatingPlan = true
      this.planCreateError = null
      try {
        const model = {
          libelle: this.planCreateModel.libelle.trim(),
          description: this.planCreateModel.description || undefined,
          responsableId: this.selectedResponsable.id,
          processusId: this.selectedProcessus ? this.selectedProcessus.id : undefined,
          dateEcheance: this.planCreateModel.dateEcheance || undefined,
        }
        const newPlanId = await this.$http.myressif.chantiers.addPlan(this.chantier.id, model)
        this.$bvModal.hide('plan-create-modal')
        this.$router.push({
          name: 'chantier-plan-detail',
          params: { chantierId: this.chantier.id, planId: newPlanId },
        })
      } catch (e) {
        this.planCreateError = "La création du plan d'action a échoué."
      } finally {
        this.creatingPlan = false
      }
    },
    // ----- Mesure d'efficacité -----
    openMesureModal() {
      const m = this.mesureEfficacite
      this.mesureModel = m
        ? {
          dateMesure: this.formatDateInput(m.dateMesure),
          indicateurs: m.indicateurs || '',
          estEfficace: m.estEfficace,
          actionsAmelioration: m.actionsAmelioration || '',
        }
        : {
          dateMesure: todayDateInput(),
          indicateurs: '',
          estEfficace: Efficacite.Oui,
          actionsAmelioration: '',
        }
      this.mesureError = null
      this.$bvModal.show('mesure-efficacite-modal')
    },
    async submitMesure() {
      const indicateurs = (this.mesureModel.indicateurs || '').trim()
      const actions = (this.mesureModel.actionsAmelioration || '').trim()
      if (!this.mesureModel.dateMesure) {
        this.mesureError = 'La date de mesure est obligatoire.'
        return
      }
      if (!indicateurs) {
        this.mesureError = "Les indicateurs d'efficacité sont obligatoires."
        return
      }
      if (this.mesureModel.estEfficace === Efficacite.Non && !actions) {
        this.mesureError = "Les actions d'amélioration sont obligatoires lorsque le chantier n'est pas efficace."
        return
      }
      if (this.savingMesure) return
      this.savingMesure = true
      this.mesureError = null
      try {
        const data = {
          dateMesure: this.mesureModel.dateMesure,
          indicateurs,
          estEfficace: this.mesureModel.estEfficace,
          actionsAmelioration: this.mesureModel.estEfficace === Efficacite.Non ? actions : undefined,
        }
        await this.$http.myressif.chantiers.setMesureEfficacite(this.chantier.id, data)
        this.$bvModal.hide('mesure-efficacite-modal')
        await this.loadChantier()
      } catch (e) {
        this.mesureError = "L'enregistrement de la mesure a échoué."
      } finally {
        this.savingMesure = false
      }
    },
    async saveMesureThenCreateChild() {
      // Enregistre la mesure (ferme la modale en cas de succès) PUIS ouvre la création d'un
      // chantier de suivi pré-rempli avec ce chantier comme parent (le lien est posé à la création).
      await this.submitMesure()
      if (!this.mesureError) this.openCreateFollowUp()
    },
    confirmDeleteMesure() {
      this.$bvModal
        .msgBoxConfirm("Supprimer la mesure d'efficacité de ce chantier ?", {
          title: 'Confirmation de suppression',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => { if (value === true) this.doDeleteMesure() })
    },
    async doDeleteMesure() {
      try {
        await this.$http.myressif.chantiers.deleteMesureEfficacite(this.chantier.id)
        await this.loadChantier()
      } catch (e) {
        // silencieux
      }
    },
    // ----- Notes libres (carte éditée en ligne) -----
    startEditNotes() {
      this.notesDraft = this.chantier.notes || ''
      this.notesError = null
      this.editingNotes = true
    },
    cancelEditNotes() {
      this.editingNotes = false
      this.notesError = null
    },
    async submitNotes() {
      if (this.savingNotes) return
      this.savingNotes = true
      this.notesError = null
      try {
        await this.$http.myressif.chantiers.updateNotes(this.chantier.id, this.notesDraft)
        this.editingNotes = false
        await this.loadChantier()
      } catch (e) {
        this.notesError = "L'enregistrement des notes a échoué."
      } finally {
        this.savingNotes = false
      }
    },
    // ----- Filiation (picker chantier parent / création d'un enfant de suivi) -----
    async searchChantiers(params) {
      // Même contrat de pagination que searchUtilisateurs : reset → page 1, sinon on concatène.
      const search = (params && params.search) || ''
      const reset = !params || params.reset
      const pageNumber = reset ? 1 : ((params && params.pageNumber) || 1)
      try {
        const response = await this.$http.myressif.chantiers.search({
          pageNumber, pageSize: 10, search: search || undefined,
        })
        const items = (response && response.items) || []
        this.listChantiers = reset
          ? items
          : this.listChantiers.concat(items.filter(ri => !this.listChantiers.some(e => e.id === ri.id)))
        this.chantiersTotalCount = (response && response.totalCount) || this.listChantiers.length
      } catch (e) {
        if (reset) { this.listChantiers = []; this.chantiersTotalCount = 0 }
      }
      return this.listChantiers
    },
    openCreateFollowUp() {
      // Ouvre la création d'un chantier de suivi avec CE chantier comme parent (lien posé à la création).
      this.$refs.createModal.open({
        parent: { id: this.chantier.id, nom: this.chantier.nom },
        categorieId: this.chantier.categorieId,
        origineId: this.chantier.origineId,
        responsable: this.chantier.responsable || null,
      })
    },
    onFollowUpCreated(newChantierId) {
      // Le nouveau chantier porte déjà le parent : naviguer vers lui.
      this.$router.push({ name: 'chantier-detail', params: { id: newChantierId } })
    },
    async downloadRecapPdf() {
      this.loadingRecap = true
      try {
        const response = await this.$http.myressif.chantiers.downloadRecapPdf(this.chantier.id)
        const blob = new Blob([response], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const slug = (this.chantier.nom || 'chantier')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `recapitulatif-${slug || 'chantier'}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$bvModal.msgBoxOk('Impossible de générer le PDF récapitulatif.', {
          title: 'Erreur', okVariant: 'danger', centered: true,
        })
      } finally {
        this.loadingRecap = false
      }
    },
    openAvancementsModal() {
      // Période par défaut : début du chantier -> aujourd'hui (sinon les 30 derniers jours).
      const today = todayDateInput()
      let defaultStart = this.formatDateInput(this.chantier && this.chantier.dateDebut)
      if (!defaultStart) {
        defaultStart = daysAgoDateInput(30)
      }
      this.avancementsDateDebut = defaultStart
      this.avancementsDateFin = today
      this.$bvModal.show('modal-avancements-pdf')
    },
    async downloadAvancementsPdf() {
      if (!this.avancementsDateDebut || !this.avancementsDateFin) return
      this.loadingAvancements = true
      try {
        const response = await this.$http.myressif.chantiers.downloadAvancementsPdf(
          this.chantier.id,
          this.avancementsDateDebut,
          this.avancementsDateFin,
        )
        const blob = new Blob([response], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const slug = (this.chantier.nom || 'chantier')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `avancements-${slug || 'chantier'}-${this.avancementsDateDebut}_${this.avancementsDateFin}.pdf`,
        )
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$bvModal.hide('modal-avancements-pdf')
      } catch (e) {
        this.$bvModal.msgBoxOk("Impossible de générer le PDF d'avancements.", {
          title: 'Erreur', okVariant: 'danger', centered: true,
        })
      } finally {
        this.loadingAvancements = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-yellow:  #fcf5a6;
$at-orange:  #ff6131;
$at-beige:   #fffced;
$at-ink-900: #0F1F1F;
$at-ink-700: #2A3F3F;
$at-ink-500: #5A7373;
$at-ink-100: #E4ECEC;

.chantier-back {
  display: inline-flex;
  align-items: center;
  color: $at-ink-500;
  font-size: 13px;
  margin-bottom: 12px;
  text-decoration: none;
  &:hover { color: $at-orange; }
}

.chantier-state {
  text-align: center;
  padding: 48px 16px;
  color: $at-ink-500;
  background: #fff;
  border: 1px dashed $at-ink-100;
  border-radius: 10px;

  &--error {
    color: $at-orange;
    border-color: rgba(255, 97, 49, .3);
  }
}

.chantier-empty-inline {
  color: $at-ink-500;
  font-style: italic;
}

.chantier-livrables {
  margin-top: 10px;
  &__text {
    white-space: pre-line;
    margin-top: 4px;
  }
}

// Hero (reuse Home pattern)
.ataviti-home {
  &__banner {
    background: $at-yellow;
    border-radius: 12px;
    padding: 28px 32px;
    margin-bottom: 24px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  &__banner-eyebrow {
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin: 0 0 8px;
  }
  &__title {
    font-family: 'Iowan Old Style', 'Palatino Linotype', Georgia, serif;
    font-size: 28px;
    color: $at-green;
    font-weight: 500;
    margin: 0 0 14px;
    letter-spacing: -.01em;
    line-height: 1.2;
  }
}

.chantier-header {
  &__meta-groups {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 6px;
  }
  &__group {                       // label au-dessus des pastilles
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__group-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: $at-ink-700;            // lisible sur le fond jaune ($at-ink-500 trop clair)
  }
  &__responsable {
    display: inline-flex;
    align-items: center;
    margin-top: 16px;
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
  }
  &__badges {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 0;
  }
  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  &__cat-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 999px;
  }
  &__origine {
    background: rgba(15, 31, 31, .08);
    color: $at-ink-700;
  }
  &__statut {
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 999px;
  }
  &__title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 14px;

    .ataviti-home__title { margin-bottom: 0; }
  }
  &__visibilite {                  // indicateur d'accès discret, à côté du titre (neutre pour Public comme Privé)
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: $at-ink-700;
    background: rgba(15, 31, 31, .07);
    border-radius: 999px;
    padding: 3px 10px;
    white-space: nowrap;
  }
}

.chantier-warning {
  display: flex;
  align-items: center;
  background: rgba(240, 168, 48, .12);
  border: 1px solid rgba(240, 168, 48, .35);
  color: #8a5a0a;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 18px;
  font-size: 13px;
}

.chantier-section {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 18px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;

    .chantier-section__title { margin-bottom: 0; }
  }
  &__title {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: $at-ink-900;
    font-weight: 600;
    margin: 0 0 14px;
  }
  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(28, 77, 77, .10);
    color: $at-green;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    margin-left: 8px;
  }
  &__text {
    color: $at-ink-700;
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
  }
  &__meta {
    display: inline-flex;
    align-items: center;
    color: $at-ink-500;
    font-size: 13px;
    margin-top: 10px;
    margin-right: 16px;
  }
}

.chantier-participants {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}
.chantier-participant {
  display: flex;
  align-items: center;
  background: $at-beige;
  border: 1px solid $at-ink-100;
  border-radius: 999px;
  padding: 8px 16px;
  min-width: 160px;

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: $at-ink-900;
    line-height: 1.1;
  }
}

.chantier-plans {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.chantier-plan-card {
  background: $at-beige;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 16px 18px;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &:hover {
    border-color: $at-green;
    box-shadow: 0 4px 14px rgba(28, 77, 77, .08);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }
  &__title {
    font-size: 15px;
    color: $at-ink-900;
    font-weight: 600;
    margin: 0;
    flex: 1;
    line-height: 1.3;
  }
  &__processus {
    background: rgba(255, 97, 49, .12);
    color: $at-orange;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
  }
  &__statut {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
  }
  &__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }
  &__responsable {
    display: flex;
    align-items: center;
  }
  &__responsable-label {
    font-size: 10px;
    color: $at-ink-500;
    text-transform: uppercase;
    letter-spacing: .04em;
  }
  &__responsable-name {
    font-size: 13px;
    color: $at-ink-900;
    font-weight: 600;
  }
  &__metrics {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }
  &__metric {
    display: inline-flex;
    align-items: center;
    color: $at-ink-500;
    font-size: 12px;
  }
  &__cta {
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
  }
}

.chantier-liens-group {
  margin-top: 12px;

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: $at-ink-500;
    font-weight: 600;
    margin-bottom: 6px;
  }
}
.chantier-liens {
  list-style: none;
  padding: 0;
  margin: 0;
}
.chantier-lien {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  flex-wrap: wrap;

  &__link {
    font-weight: 600;
    color: $at-green;
    &:hover { color: $at-orange; }
  }
}
</style>
